import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import BrandLogo from "../components/BrandLogo";
import { Button } from "../components/ui/button";
import { CheckCircle2, ArrowRight, Clock, Phone, Mail, ShieldCheck } from "lucide-react";
import ChatButton from "../components/ChatButton";
import PageMeta from "../components/PageMeta";

/**
 * /application/main — landing after a successful lead submission.
 * Shows a thank-you screen with the returned customerId and next steps.
 */
export default function ApplicationMain() {
  const [params] = useSearchParams();
  const [customerId, setCustomerId] = useState("");

  useEffect(()=>{
    setCustomerId(params.get("customerid") || params.get("customerId") || "");
  },[params]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-slate-50" data-testid="application-main-page">
      <PageMeta
        title="Thank You — Prime Wiki Services"
        description="Your Wikipedia page inquiry has been received. A specialist will reach out within the next business hour with a confidential consultation."
        noindex={true}
      />
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Link to="/"><BrandLogo /></Link>
          <Link to="/" className="text-sm font-semibold text-slate-600 hover:text-[#0A2540]">Back to website</Link>
        </div>
      </header>
      <main className="mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="rounded-3xl border border-slate-200 bg-white p-8 text-center shadow-xl">
          <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-emerald-50 text-emerald-600">
            <CheckCircle2 className="h-8 w-8" />
          </div>
          <h1 className="font-serif mt-5 text-3xl font-semibold leading-tight text-[#0A2540] sm:text-4xl">Thank you — we've received your request.</h1>
          <p className="mt-3 text-base text-slate-600">A Wikipedia specialist will reach out within the next business hour.</p>
          {customerId ? (
            <div className="mx-auto mt-6 inline-flex items-center gap-2 rounded-full bg-slate-100 px-4 py-2 text-sm font-medium text-[#0A2540]" data-testid="application-customer-id">
              <ShieldCheck className="h-4 w-4 text-emerald-500"/> Reference ID: <span className="font-mono">{customerId}</span>
            </div>
          ) : null}
          <div className="mt-8 grid gap-4 sm:grid-cols-3 text-left">
            {[
              {i:Clock,t:"Next step",d:"We'll call you within 60 minutes."},
              {i:Phone,t:"Prefer to call?",d:"+1 (877) 469-1642"},
              {i:Mail,t:"Prefer email?",d:"info@Primewikiservices.com"},
            ].map((x)=>(
              <div key={x.t} className="rounded-2xl border border-slate-200 p-4">
                <x.i className="h-5 w-5 text-emerald-500"/>
                <div className="mt-2 text-xs uppercase tracking-wider text-slate-500">{x.t}</div>
                <div className="mt-1 text-sm font-semibold text-[#0A2540]">{x.d}</div>
              </div>
            ))}
          </div>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/services"><Button className="rounded-full bg-[#0A2540] px-6 text-white hover:bg-[#123B6E]">Explore services <ArrowRight className="ml-2 h-4 w-4"/></Button></Link>
            <ChatButton className="rounded-full border border-slate-200 bg-white px-6 py-2.5 text-sm font-semibold text-[#0A2540]"/>
          </div>
        </div>
        <p className="mt-6 text-center text-xs text-slate-500">You'll also receive a confirmation email shortly. Please check your spam folder.</p>
      </main>
    </div>
  );
}
